import React from 'react';
import styled, { keyframes } from 'styled-components';
import SupportImg from '../../img/dyweb.png'

// --- Animations ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #2d3748;
  line-height: 1.7;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

// --- Global Styles for Headings and Buttons ---

const SectionTitle = styled.h2`
  font-size: 3rem;
  color: #1a202c;
  margin-bottom: 30px;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 1.4rem;
  color: #2d3748;
  margin-bottom: 20px;
  font-weight: 700;
  line-height: 1.4;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const CallToAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  background: linear-gradient(135deg, #d88b0a 0%, #f59e0b 100%);
  color: white;
  text-decoration: none;
  font-weight: 700;
  border-radius: 50px;
  margin-top: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(216, 139, 10, 0.3);
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(216, 139, 10, 0.4);
    
    &::before {
      left: 100%;
    }
  }

  &::after {
    content: '→';
    font-weight: bold;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(5px);
  }
`;

const ViewAllButton = styled(CallToAction)`
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  box-shadow: 0 4px 15px rgba(45, 55, 72, 0.3);

  &:hover {
    box-shadow: 0 8px 25px rgba(45, 55, 72, 0.4);
  }
`;

// --- Section Layout Components ---

const Section = styled.section`
  padding: 80px 10%;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;

  &:nth-child(even) {
    background: white;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const ContentBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 60px;
  margin-bottom: 40px;
  animation: ${fadeInUp} 0.8s ease-out;

  /* Reversing order for alternating sections */
  ${props => props.$reverse && 'direction: rtl;'}

  ${props => props.$reverse} > * {
    ${props => props.$reverse && 'direction: ltr;'}
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
    
    ${props => props.$reverse && 'direction: ltr;'}
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  p {
    font-size: 1.1rem;
    color: #4a5568;
    margin-bottom: 20px;
    line-height: 1.8;
    animation: ${fadeInUp} 0.8s ease-out 0.1s both;
  }

  strong {
    color: #2d3748;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    order: 1;
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  animation: ${float} 3s ease-in-out infinite;

  @media (max-width: 900px) {
    order: 0;
  }
`;

const IllustrationImage = styled.img`
  width: 100%;
  height: auto;
  max-width: 500px;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    border-radius: 20px;
  }
`;

// --- Special Section Styles ---

const DidYouKnowSection = styled.div`
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  color: white;
  padding: 100px 10%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.05)"><polygon points="0,0 1000,100 1000,0"></polygon></svg>');
    background-size: cover;
  }

  @media (max-width: 768px) {
    padding: 80px 5%;
  }
`;

const DidYouKnowTitle = styled.h2`
  font-size: 3.5rem;
  margin-bottom: 60px;
  font-weight: 300;
  color: white;
  position: relative;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  position: relative;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 50px;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Stat = styled.div`
  text-align: center;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const StatCircle = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  border: 10px solid #d88b0a;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2.5rem;
  font-weight: 800;
  color: #d88b0a;
  margin: 0 auto 25px auto;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  animation: ${pulse} 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
    transform: rotate(45deg);
    transition: all 0.6s ease;
  }

  &:hover {
    transform: scale(1.1);
    border-color: #f59e0b;
    color: #f59e0b;
    
    &::before {
      transform: rotate(45deg) translate(50%, 50%);
    }
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    font-size: 2rem;
  }
`;

const StatDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  opacity: 0.9;
  font-weight: 500;
  max-width: 250px;
  margin: 0 auto;
`;

const HorizontalLine = styled.hr`
  border: 0;
  height: 2px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 60px 10%;
  opacity: 0.3;
  border-radius: 2px;
`;

const FooterSection = styled.div`
  padding: 60px 10%;
  text-align: center;
  background: white;
`;

const FeatureHighlight = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  border-radius: 15px;
  margin: 30px 0;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="0,100 1000,0 1000,100"></polygon></svg>');
    background-size: cover;
  }
`;

const HighlightText = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  position: relative;
  line-height: 1.6;
`;

// --- React Component ---

const Seo2 = () => {
  return (
    <PageContainer>
      {/* 1. SEO ON PAGE OPTIMIZATION SERVICES Section */}
      <Section>
        <SectionTitle>SEO On-Page Optimization Services</SectionTitle>
        <ContentBlock>
          <TextContent>
            <p>
              <strong>SEO services, particularly on-page optimization, focus on making your website easily digestible for search engines and engaging for users.</strong> At <strong>SRK Software</strong>, we work directly on your site to improve its details in search results. It includes optimizing content, formatting, site structure, and speed.
            </p>
            <p>
              <strong>The goal of on-page optimization is to tell search engines exactly what your site's content and resources are organized, making your content easy to navigate.</strong> Unique and compelling meta descriptions and titles, coupled with relevant keywords, communicate your page's topic in search navigation.
            </p>
            <FeatureHighlight>
              <HighlightText>
                <strong>SRK Software</strong> can put your brand in the mouths of Siri and Alexa!
              </HighlightText>
            </FeatureHighlight>
            <CallToAction href="#">Get Me Started!</CallToAction>
          </TextContent>
          <ImagePlaceholder>
            <IllustrationImage 
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
              alt="SEO Optimization Services Illustration" 
            />
          </ImagePlaceholder>
        </ContentBlock>
      </Section>

      <HorizontalLine />

      {/* 2. Voice Search Optimization Section */}
      <Section>
        <ContentBlock $reverse>
          <TextContent>
            <p>
              <strong>Voice Search Optimization</strong> is about making your online content compatible with how people naturally speak instead of typing keywords. Users ask questions aloud to voice assistants like Siri or Google Assistant, so we optimize for conversational language in your content.
            </p>
            <p>
              <strong>SRK Software</strong> is dedicated to helping you adapt your website and content to this trend. We'll ensure that your website loads quickly on mobile devices and is optimized for voice search queries.
            </p>
            <FeatureHighlight>
              <HighlightText>
                <strong>SRK Software</strong> will make your site the final point of any visitor's experience!
              </HighlightText>
            </FeatureHighlight>
            <CallToAction href="#">Click Here</CallToAction>
          </TextContent>
          <ImagePlaceholder>
            <IllustrationImage 
              src={SupportImg} 
              alt="Voice Search Optimization Illustration" 
            />
          </ImagePlaceholder>
        </ContentBlock>
      </Section>

      <HorizontalLine />

      {/* 3. Local SEO Optimization Section */}
      <Section>
        <ContentBlock>
          <TextContent>
            <p>
              By using the right words and making your website easy to understand, we help search engines know precisely what you're all about. This matters when people search for something related to your business with phrases like "near me."
            </p>
            <p>
              <strong>Local SEO</strong> means making sure you're visible when more people use location-based searches. These visitors can turn into customers, readers, or fans. We focus on location-specific keywords to develop the best strategy for your business.
            </p>
            <FeatureHighlight>
              <HighlightText>
                <strong>SRK Software</strong> will let's work together to make your site the fastest, most user-friendly option available.
              </HighlightText>
            </FeatureHighlight>
            <CallToAction href="#">I'm Ready to Get Started!</CallToAction>
          </TextContent>
          <ImagePlaceholder>
            <IllustrationImage 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
              alt="Local SEO Search Results Screenshot" 
            />
          </ImagePlaceholder>
        </ContentBlock>
      </Section>

      <HorizontalLine />

      {/* 4. LINK BUILDING Section */}
      <Section>
        <SectionTitle>Link Building Strategy</SectionTitle>
        <ContentBlock $reverse>
          <TextContent>
            <p>
              <strong>Link building is like building connections on the Internet.</strong> It's about getting other trusted websites to link to your site. These links are like pathways that lead people to your site when reputable websites recommend you.
            </p>
            <p>
              Search engines, like Google, see these votes and consider your site more trustworthy and important. The more high-quality backlinks you get, especially from respected websites in your field, the higher your authority grows.
            </p>
            <p>
              They help increase your website's authority and can significantly improve its search ranking. Building quality links is an essential part of making your website more visible and credible online.
            </p>
            <ViewAllButton href="#">View All Results</ViewAllButton>
          </TextContent>
          <ImagePlaceholder>
            <IllustrationImage 
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
              alt="Link Building Illustration" 
            />
          </ImagePlaceholder>
        </ContentBlock>
      </Section>

      {/* 5. Did You Know? Section */}
      <DidYouKnowSection>
        <DidYouKnowTitle>Did You Know?</DidYouKnowTitle>
        <StatsContainer>
          <Stat>
            <StatCircle>0.78%</StatCircle>
            <StatDescription>
              of Google searches click on results from the second page
            </StatDescription>
          </Stat>
          <Stat>
            <StatCircle>93%</StatCircle>
            <StatDescription>
              of online activities start with a search engine
            </StatDescription>
          </Stat>
          <Stat>
            <StatCircle>63%</StatCircle>
            <StatDescription>
              of users conduct research before making a purchase
            </StatDescription>
          </Stat>
          <Stat>
            <StatCircle>61%</StatCircle>
            <StatDescription>
              of marketers give priority to developing their organic presence
            </StatDescription>
          </Stat>
        </StatsContainer>
      </DidYouKnowSection>

      {/* 6. Footer Section */}
      <FooterSection>
        <SectionTitle style={{ marginBottom: '20px', fontSize: '2.5rem' }}>
          Ready to Boost Your Online Presence?
        </SectionTitle>
        <p style={{ fontSize: '1.2rem', color: '#4a5568', marginBottom: '30px' }}>
          Let's work together to create a comprehensive SEO strategy that drives results.
        </p>
        <CallToAction href="#contact" style={{ margin: '0 auto' }}>
          Start Your SEO Journey
        </CallToAction>
      </FooterSection>
    </PageContainer>
  );
};

export default Seo2;